import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

import { useChatParticipant } from './useChatParticipant'
import { useChats } from './useChats'

import type { IConversation } from '../types/conversation.types'

interface UseConversationActionsOptions {
  onCleared?: () => void | Promise<void>
}

export const useConversationActions = (
  conversation: MaybeRefOrGetter<IConversation | null | undefined>,
  conversationId?: MaybeRefOrGetter<string | undefined>,
  options?: UseConversationActionsOptions
) => {
  const chats = useChats()
  const { getCurrentUserId } = useChatParticipant()
  const { CPNavigateTo } = useCPNavigation()

  const resolvedConversation = computed(() => toValue(conversation))
  const resolvedConversationId = computed(() => {
    return toValue(conversationId) || resolvedConversation.value?._id
  })

  const blockOverride = ref<boolean | null>(null)

  const isBlockedFromConversation = computed(() => {
    const currentConversation = resolvedConversation.value
    if (!currentConversation) return false
    if (currentConversation.blockedByCurrentUser !== undefined) {
      return !!currentConversation.blockedByCurrentUser
    }
    if (currentConversation.isBlocked !== undefined) {
      return !!currentConversation.isBlocked
    }
    if (currentConversation.status === 'BLOCKED') {
      return true
    }

    const platformUserId = getCurrentUserId()
    return !!(platformUserId && currentConversation.blockedBy?.includes(platformUserId))
  })

  const isBlockedByCurrentUser = computed(() => {
    return blockOverride.value ?? isBlockedFromConversation.value
  })

  watch(isBlockedFromConversation, value => {
    if (blockOverride.value !== null && blockOverride.value === value) {
      blockOverride.value = null
    }
  })

  watch(resolvedConversationId, () => {
    blockOverride.value = null
  })

  const blockConversation = async () => {
    const id = resolvedConversationId.value
    if (!id) return
    const previousOverride = blockOverride.value
    blockOverride.value = true
    try {
      await chats.blockConversation(id)
    } catch (error) {
      blockOverride.value = previousOverride
      console.error('Failed to block conversation:', error)
    }
  }

  const unblockConversation = async () => {
    const id = resolvedConversationId.value
    if (!id) return
    const previousOverride = blockOverride.value
    blockOverride.value = false
    try {
      await chats.unblockConversation(id)
    } catch (error) {
      blockOverride.value = previousOverride
      console.error('Failed to unblock conversation:', error)
    }
  }

  const deleteConversation = async () => {
    const id = resolvedConversationId.value
    if (!id) return
    try {
      await chats.deleteConversation(id)
      if (options?.onCleared) {
        await options.onCleared()
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
    }
  }

  const viewBlockedList = async () => {
    try {
      await CPNavigateTo({ name: 'account-settings' })
    } catch (error) {
      console.error('Failed to navigate to blocked list:', error)
    }
  }

  return {
    isBlockedByCurrentUser,
    blockConversation,
    unblockConversation,
    deleteConversation,
    viewBlockedList,
  }
}
